import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { organizationsApi } from '@/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { MasterLocaleSelects, countryLabel } from '@/components/master/MasterLocaleSelects';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import {
  CURRENT_ORG_QUERY_KEY,
  resolveCountryCode,
  useCurrentOrganization,
} from '@/hooks/useCurrentOrganization';
import { useMasterData } from '@/hooks/useMasterData';
import type { OrganizationRequest, OrganizationResponse } from '@/types/organization';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

function orgToForm(
  data: OrganizationResponse,
  countries: { code: string; label: string }[],
): OrganizationRequest {
  const resolvedCountry = resolveCountryCode(data, countries);
  return {
    name: data.name,
    email: data.email,
    phone: data.phone,
    gstNumber: data.gstNumber,
    address: data.address,
    city: data.city,
    state: data.state,
    country: data.country,
    countryCode: resolvedCountry,
    pincode: data.pincode,
    timezone: data.timezone ?? 'Asia/Kolkata',
    currencyCode: data.currencyCode ?? 'INR',
    subscriptionPlan: data.subscriptionPlan,
  };
}

export function OrganizationProfilePage() {
  const queryClient = useQueryClient();
  const { countries } = useMasterData();
  const { data: org, isError, error } = useCurrentOrganization();
  const [form, setForm] = useState<OrganizationRequest | null>(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!org || dirty) return;
    setForm(orgToForm(org, countries));
  }, [org, countries.length, dirty]);

  useEffect(() => {
    if (isError && error) {
      showApiError(error, 'Failed to load organization');
    }
  }, [isError, error]);

  const save = async () => {
    if (!org?.id || !form) return;
    setSaving(true);
    try {
      const payload: OrganizationRequest = {
        ...form,
        country: form.countryCode ? countryLabel(countries, form.countryCode) : form.country,
        countryCode: form.countryCode,
      };
      const updated = await organizationsApi.update(org.id, payload);
      queryClient.setQueryData(CURRENT_ORG_QUERY_KEY, updated);
      setForm(orgToForm(updated, countries));
      setDirty(false);
      notify.success('Profile updated');
    } catch (err) {
      showApiError(err, 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <div>Loading...</div>;

  return (
    <div>
      <PageHeader title="Organization Profile" breadcrumbs={[{ label: 'Org', to: '/org' }, { label: 'Profile' }]} />
      <div className="card max-w-2xl p-6">
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Name"
            value={form.name}
            onChange={(e) => {
              setDirty(true);
              setForm({ ...form, name: e.target.value });
            }}
          />
          <Input
            label="Email"
            value={form.email}
            onChange={(e) => {
              setDirty(true);
              setForm({ ...form, email: e.target.value });
            }}
          />
          <Input
            label="Phone"
            value={form.phone ?? ''}
            onChange={(e) => {
              setDirty(true);
              setForm({ ...form, phone: e.target.value });
            }}
          />
          <Input
            label="GST"
            value={form.gstNumber ?? ''}
            onChange={(e) => {
              setDirty(true);
              setForm({ ...form, gstNumber: e.target.value });
            }}
          />
          <Input
            label="Address"
            value={form.address ?? ''}
            onChange={(e) => {
              setDirty(true);
              setForm({ ...form, address: e.target.value });
            }}
            className="sm:col-span-2"
          />
          <Input
            label="City"
            value={form.city ?? ''}
            onChange={(e) => {
              setDirty(true);
              setForm({ ...form, city: e.target.value });
            }}
          />
          <Input
            label="State"
            value={form.state ?? ''}
            onChange={(e) => {
              setDirty(true);
              setForm({ ...form, state: e.target.value });
            }}
          />
          <Input
            label="Pincode"
            value={form.pincode ?? ''}
            onChange={(e) => {
              setDirty(true);
              setForm({ ...form, pincode: e.target.value });
            }}
          />
        </div>
        <MasterLocaleSelects
          className="mt-4"
          value={{
            countryCode: form.countryCode ?? '',
            currencyCode: form.currencyCode ?? '',
            timezone: form.timezone ?? '',
          }}
          onChange={(patch) => {
            setDirty(true);
            setForm({ ...form, ...patch });
          }}
          countryRequired
          currencyRequired
          timezoneRequired
        />
        {org && (
          <p className="mt-4 text-sm text-slate-500">
            Plan: {org.subscriptionPlan} · Status: {org.subscriptionStatus}
          </p>
        )}
        <Button className="mt-6" onClick={save} loading={saving}>
          Save changes
        </Button>
      </div>
    </div>
  );
}
